import * as THREE from 'three';
import {
    createSnapContext,
    describeProduct,
    SnapOptions,
} from 'components/customer/RoomPlanner/helpers/snapping/context';
import {routeProducts} from 'components/customer/RoomPlanner/helpers/snapping/routing';
import {
    isSnapRejection,
    SnapCandidate,
    SnapOutcome,
    SnapRejection,
} from 'components/customer/RoomPlanner/helpers/snapping/types';

/**
 * The best snap outcome for a drag across every other product: each is routed
 * to the strategy for its pair (snapping/routing.ts), and the nearest
 * candidate wins. With no candidate, a rejection, if any pair was rejected,
 * so the caller still applies its snap state. Changes nothing: the caller
 * moves the product and applies the outcome's snap state with
 * applySnapState.
 * @param {THREE.Group} cabinetGroup The dragged product.
 * @param {THREE.Vector3} targetPos Where the drag puts it.
 * @param {THREE.Group[]} productsRef Every product in the room.
 * @param {SnapOptions} options The camera, overlaps and walls.
 * @return {SnapOutcome} The winning candidate, a rejection, or null.
 */
export const handleSnapping = (
    cabinetGroup: THREE.Group<THREE.Object3DEventMap>,
    targetPos: THREE.Vector3,
    productsRef: THREE.Group<THREE.Object3DEventMap>[],
    options: SnapOptions
): SnapOutcome => {
    const ctx = createSnapContext(
        cabinetGroup,
        targetPos,
        productsRef,
        options
    );

    // The nearest candidate across every product. Each algorithm measures
    // its own distance, and the router only compares them.
    // Use cast here because the assignment happens in the callback which
    // TypeScript doesn't follow.
    let best = null as SnapCandidate | null;
    const consider = (candidate: SnapCandidate | null) => {
        if (candidate === null) return;
        if (best === null || candidate.distance < best.distance) {
            best = candidate;
        }
    };

    // A rejected pair, whose snap state stands when no winner carries one.
    // Use cast here same as above.
    let rejection = null as SnapRejection | null;

    routeProducts(describeProduct(cabinetGroup), productsRef ?? []).forEach(
        ({other, pair, route}) => {
            if (route.strategy === null) return;

            const outcome = route.strategy(cabinetGroup, other, ctx, pair);
            if (isSnapRejection(outcome)) {
                rejection = outcome;
                return;
            }

            consider(outcome);
        }
    );

    if (best === null) return rejection;
    if (best.snapState === undefined && rejection !== null) {
        return {...best, snapState: rejection.snapState};
    }

    return best;
};
